import { useAuth } from 'context/useAuth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  doc,
  documentId,
  endAt,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  startAfter,
  startAt,
} from 'firebase/firestore';
import { Device } from 'types/Device';

export type DeviceSortLabel = 'Latest' | 'A - Z' | 'Z - A';

interface UseDevicesSearchOptions {
  query?: string;
  sort?: DeviceSortLabel;
  pageSize?: number;
}

interface UseDevicesSearchResult {
  devices: Device[];
  loading: boolean;
  error: string;
  pageIndex: number;
  hasNext: boolean;
  hasPrev: boolean;
  next: () => void;
  prev: () => void;
}

function normaliseToken(input: string): string {
  return (input || '').trim().toLocaleLowerCase();
}

function mapDocToDevice(docId: string): Device {
  return { id: docId, active: true, serialNumber: docId };
}

// Max docs we'll pull for an in-memory search. The devices collection has no
// `search.keywords` denormalisation yet, so id / remote-serial lookups must
// run client-side. Keep this bounded to avoid runaway reads on big tenants.
const CLIENT_SEARCH_FETCH_LIMIT = 5000;

function extractRemoteSerials(data: any): string[] {
  const rcs = data?.ConfigurationStatusLog?.configuration?.RemotesConfiguration?.RemoteConfigurations;
  if (!Array.isArray(rcs)) return [];
  const out: string[] = [];
  for (const rc of rcs) {
    const sn = rc?.RemoteSerialNumber;
    if (typeof sn === 'string' && sn.trim()) out.push(sn.trim());
  }
  return out;
}

function buildHaystack(docId: string, data: any): string {
  const remotes = extractRemoteSerials(data).join('~');
  return `${docId}~${remotes}`.toLocaleLowerCase();
}

export default function useDevicesSearch(
  options: UseDevicesSearchOptions = {}
): UseDevicesSearchResult {
  const { query: rawQuery = '', sort = 'Latest', pageSize = 10 } = options;
  const { firestore } = useAuth();

  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [hasNext, setHasNext] = useState(false);

  // Server-side cursor pagination (used when there is no search query).
  const cursorsRef = useRef<Array<QueryDocumentSnapshot | null>>([null]);
  const lastDocRef = useRef<QueryDocumentSnapshot | null>(null);

  // Client-side filtered cache (used when there IS a search query).
  const filteredRef = useRef<Device[]>([]);

  // Tracks whether the component is still mounted, and which fetch is the
  // latest in flight. Older responses are dropped to prevent races.
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const normalisedQuery = useMemo(() => normaliseToken(rawQuery), [rawQuery]);

  // Server-side paginated fetch — only used when no search query is active.
  // The device doc has no createdAt column, so we always orderBy __name__.
  // Descending requires a custom index, so 'Latest' falls back to ascending.
  const fetchServerPage = useCallback(
    async (targetPageIndex: number) => {
      if (!firestore) return;
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError('');

      try {
        const constraints: QueryConstraint[] = [];
        if (sort === 'Z - A') {
          constraints.push(orderBy('__name__', 'desc'));
        } else {
          constraints.push(orderBy('__name__', 'asc'));
        }

        const cursor = cursorsRef.current[targetPageIndex] ?? null;
        if (cursor) {
          constraints.push(startAfter(cursor));
        }

        constraints.push(limit(pageSize + 1));

        const q = query(collection(firestore, 'devices'), ...constraints);
        const snap = await getDocs(q);

        if (!mountedRef.current || requestId !== requestIdRef.current) return;

        const docs = snap.docs;
        const pageDocs = docs.slice(0, pageSize);
        const hasNextPage = docs.length > pageSize;

        setDevices(pageDocs.map((d) => mapDocToDevice(d.id)));
        setHasNext(hasNextPage);
        setPageIndex(targetPageIndex);
        lastDocRef.current = pageDocs[pageDocs.length - 1] ?? null;
      } catch (err: any) {
        if (!mountedRef.current || requestId !== requestIdRef.current) return;
        setError(err?.message || String(err));
        setDevices([]);
        setHasNext(false);
      } finally {
        if (mountedRef.current && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [firestore, sort, pageSize]
  );

  // Slice the in-memory filtered list to the requested page.
  const showFilteredPage = useCallback(
    (targetPageIndex: number) => {
      const all = filteredRef.current;
      const start = targetPageIndex * pageSize;
      const end = start + pageSize;
      setDevices(all.slice(start, end));
      setHasNext(end < all.length);
      setPageIndex(targetPageIndex);
    },
    [pageSize]
  );

  // Client-side search: fetch (up to CLIENT_SEARCH_FETCH_LIMIT) and filter on
  // device id (= primary serial) and every nested remote serial number.
  //
  // We ALSO run two targeted lookups so a known id is always findable even if
  // the alphabetical scan window (5000 docs) wouldn't otherwise reach it:
  //   1. Exact doc-id getDoc(rawQuery)
  //   2. Doc-id prefix range using documentId() >= q AND <= q + '\uf8ff'
  // Results from all three sources are merged & deduped by id.
  const runClientSearch = useCallback(async () => {
    if (!firestore || !normalisedQuery) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');

    try {
      const rawTrim = (rawQuery || '').trim();
      const matchesById = new Map<string, Device>();

      // 1. Exact doc-id hit (case-sensitive, as Firestore stores it).
      if (rawTrim) {
        try {
          const exact = await getDoc(doc(firestore, 'devices', rawTrim));
          if (exact.exists()) {
            matchesById.set(exact.id, mapDocToDevice(exact.id));
          }
        } catch {
          // ignore — invalid id chars etc.
        }
      }

      // 2. Doc-id prefix range.
      if (rawTrim) {
        try {
          const prefixQ = query(
            collection(firestore, 'devices'),
            orderBy(documentId()),
            startAt(rawTrim),
            endAt(rawTrim + '\uf8ff'),
            limit(CLIENT_SEARCH_FETCH_LIMIT),
          );
          const prefixSnap = await getDocs(prefixQ);
          for (const d of prefixSnap.docs) {
            if (!matchesById.has(d.id)) {
              matchesById.set(d.id, mapDocToDevice(d.id));
            }
          }
        } catch {
          // ignore
        }
      }

      // 3. Bounded scan for nested remote-serial matches (and substring id hits).
      const constraints: QueryConstraint[] = [
        orderBy('__name__', sort === 'Z - A' ? 'desc' : 'asc'),
        limit(CLIENT_SEARCH_FETCH_LIMIT),
      ];
      const q = query(collection(firestore, 'devices'), ...constraints);
      const snap = await getDocs(q);

      if (!mountedRef.current || requestId !== requestIdRef.current) return;

      for (const d of snap.docs) {
        if (matchesById.has(d.id)) continue;
        if (buildHaystack(d.id, d.data()).indexOf(normalisedQuery) > -1) {
          matchesById.set(d.id, mapDocToDevice(d.id));
        }
      }

      const matches = Array.from(matchesById.values());
      // Stable sort to match requested order.
      matches.sort((a, b) => (sort === 'Z - A' ? b.id.localeCompare(a.id) : a.id.localeCompare(b.id)));

      filteredRef.current = matches;
      setDevices(matches.slice(0, pageSize));
      setHasNext(matches.length > pageSize);
      setPageIndex(0);
    } catch (err: any) {
      if (!mountedRef.current || requestId !== requestIdRef.current) return;
      setError(err?.message || String(err));
      filteredRef.current = [];
      setDevices([]);
      setHasNext(false);
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [firestore, normalisedQuery, rawQuery, sort, pageSize]);

  useEffect(() => {
    cursorsRef.current = [null];
    lastDocRef.current = null;
    filteredRef.current = [];
    if (normalisedQuery) {
      runClientSearch();
    } else {
      fetchServerPage(0);
    }
  }, [normalisedQuery, runClientSearch, fetchServerPage]);

  const next = useCallback(() => {
    if (!hasNext || loading) return;
    const nextIndex = pageIndex + 1;
    if (normalisedQuery) {
      showFilteredPage(nextIndex);
      return;
    }
    if (cursorsRef.current.length <= nextIndex) {
      cursorsRef.current[nextIndex] = lastDocRef.current;
    }
    fetchServerPage(nextIndex);
  }, [hasNext, loading, pageIndex, normalisedQuery, showFilteredPage, fetchServerPage]);

  const prev = useCallback(() => {
    if (pageIndex <= 0 || loading) return;
    if (normalisedQuery) {
      showFilteredPage(pageIndex - 1);
      return;
    }
    fetchServerPage(pageIndex - 1);
  }, [pageIndex, loading, normalisedQuery, showFilteredPage, fetchServerPage]);

  return {
    devices,
    loading,
    error,
    pageIndex,
    hasNext,
    hasPrev: pageIndex > 0,
    next,
    prev,
  };
}
